import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import _ from 'lodash'
import { Box, TextField, Grid, Typography, IconButton, InputAdornment } from '@material-ui/core'
import { useFormik } from 'formik'
import { useStyles } from './style'
import * as yup from 'yup'
import CustomDialog from '../../Components/CustomDialog'
import { changePassword, clearMessage, getCurrentUser } from '../../AppRedux/actions/Auth'
import DisplayMessage from '../../Components/DisplayMessage'
import { accountService } from '../../Services/AccountService/AccountService'
import * as messageTypes from '../../utils/messageTypes'
import { Loader, AppButton } from '../../Components'
import { COMMON } from '../../utils/constants'
import Icon from '../../utils/icons'
import { Visibility, VisibilityOff } from '@material-ui/icons'

const validationSchema = yup.object({
  name: yup
    .string('Enter fullname')
    .required('Full Name is required'),
  email: yup
    .string('Enter your email')
    .email('Enter a valid email')
    .required('Email is required'),
  phone: yup
    .string('Enter phone number')
    .matches(/(^[0-9]{10})$/, 'Enter valid phone number')
    .required('Phone number is required'),
})

const validatePasswordSchema = yup.object({
  old_password: yup
    .string('Enter password')
    .required('Please Enter your password')
    .min(8, 'Password should be of minimum 8 characters length')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      "Must contain at least 8 Characters, One Uppercase, One Lowercase, One Number and one of the following special case characters @, $, !, %, *, ?, &"
    ),
  password: yup
    .string('Enter password')
    .required('Please Enter your new password')
    .min(8, 'Password should be of minimum 8 characters length')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      "Must contain at least 8 Characters, One Uppercase, One Lowercase, One Number and one of the following special case characters @, $, !, %, *, ?, &"
    ),
  matching_password: yup
    .string('Confirm password')
    .required('Passwords not matching')
    .oneOf([yup.ref('password'), null], 'Passwords not matching')
})


function PersonalDetails(props) {
  const classes = useStyles()
  const [openChangePassword, setOpenChangePassword] = useState()
  const [openMessage, setOpenMessage] = useState()
  const [userDetail, setUserDetail] = useState({})
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [loading, setLoading] = useState(true)
  const [showPassword, setShowPassword] = useState(false)

  const formik = useFormik({
    initialValues: {
      name: userDetail.name || '',
      email: props.currentUser.id,
      phone: userDetail.phone || ''
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      updatePersonalDetails(values)
    },
    enableReinitialize: true,
  })

  const changePasswordForm = useFormik({
    initialValues: {
      old_password: '',
      password: '',
      matching_password: '',
    },
    validationSchema: validatePasswordSchema,
    onSubmit: (values, { resetForm }) => {
      let obj = { ...values, email: props.currentUser.id }
      props.clearMessage()
      props.changePassword(obj)
      setOpenMessage(true)
      resetForm({ values: '' })
      setTimeout(() => {
        setOpenMessage(false)
      }, 5000)
    },
  })

  const updatePersonalDetails = (values) => {
    const { currentUser } = props
    setLoading(true)
    accountService.updatePersonalDetails(currentUser, values)
      .then(userData => {
        setLoading(false)
        setOpen(true)
        setMessage('Information updated successfully')
        setMessageType(messageTypes.SUCCESS)
        props.getCurrentUser()
      })
      .catch(error => {
        setLoading(false)
        setOpen(true)
        setMessage('Failed to update information')
        setMessageType(messageTypes.ERROR)
      })
  }

  const getUser = () => {
    const { currentUser } = props
    let params = { account_id: currentUser.account_id, email: currentUser.id }
    accountService.getUser(params)
      .then(userData => {
        setUserDetail(userData)
        setLoading(false)
      })
      .catch(error => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (props.messageType === messageTypes.SUCCESS) {
      setOpenChangePassword(false)
    }
  }, [props.message, props.messageType])

  useEffect(() => {
    getUser()
  }, [])

  useEffect(() => {
    if (!openChangePassword) {
      changePasswordForm.resetForm()
    }
  }, [openChangePassword])


  const getChangePasswordForm = () => {
    return <form onSubmit={changePasswordForm.handleSubmit}>
      <Grid container>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="password"
            className={classes.marginB}
            name="old_password"
            label="Enter Current Password"
            type="password"
            value={changePasswordForm.values.old_password}
            onChange={changePasswordForm.handleChange}
            error={changePasswordForm.touched.old_password && Boolean(changePasswordForm.errors.old_password)}
            helperText={changePasswordForm.touched.old_password && changePasswordForm.errors.old_password}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="new-password"
            className={classes.marginY}
            name="password"
            label="New Password"
            type="password"
            value={changePasswordForm.values.password}
            onChange={changePasswordForm.handleChange}
            error={changePasswordForm.touched.password && Boolean(changePasswordForm.errors.password)}
            helperText={changePasswordForm.touched.password && changePasswordForm.errors.password}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="confirm-password"
            className={classes.marginT}
            name="matching_password"
            label="Confirm Password"
            type={showPassword ? "text" : "password"}
            InputProps={{
              endAdornment: <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((show) => !show)}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>,
            }}
            value={changePasswordForm.values.matching_password}
            onChange={changePasswordForm.handleChange}
            error={changePasswordForm.touched.matching_password && Boolean(changePasswordForm.errors.matching_password)}
            helperText={changePasswordForm.touched.matching_password && changePasswordForm.errors.matching_password}
          />
        </Grid>
      </Grid>
      <div className={classes.formButtonContainer}>
        <AppButton className={`${classes.button} ${classes.primaryButton}`} type="submit" text="Submit" />
      </div>
    </form>
  }

  const handleMessageClose = (event, reason) => {
    if (reason === COMMON.CLICKAWAY) {
      return
    }

    setOpen(false)
    setOpenMessage(false)
  }

  const renderIcon = (errors, touched) => {
    return touched ? _.isUndefined(errors) ? <Icon src="circleTick" /> : <Icon src="circleCross" /> : <></>
  }

  return <>
    <div>
      <Box>
        <Loader loading={loading || props.loading} />
        <CustomDialog open={openChangePassword} title="Change Password" fullWidth handleClose={() => setOpenChangePassword(false)} content={getChangePasswordForm()} />
        {props.message && props.messageType && <DisplayMessage open={openMessage} message={props.message} messageType={props.messageType} handleClose={handleMessageClose} />}
        <DisplayMessage open={open} message={message} messageType={messageType} handleClose={handleMessageClose} />
        <Box className={classes.formContainer}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={4}>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  id="fullname"
                  name="name"
                  label="Full Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={(e) => formik.setFieldValue('name', e.target.value.trim())}
                  InputProps={{
                    endAdornment: renderIcon(formik.errors.name, formik.touched.name),
                  }}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  type="email"
                  disabled
                  value={formik.values.email}
                  onChange={(e) => formik.setFieldValue('email', e.target.value.trim())}
                  InputProps={{
                    endAdornment: renderIcon(formik.errors.email, formik.touched.email),
                  }}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  id="phone"
                  name="phone"
                  label="Phone"
                  value={formik.values.phone}
                  onChange={(e) => formik.setFieldValue('phone', e.target.value.trim())}
                  InputProps={{
                    endAdornment: renderIcon(formik.errors.phone, formik.touched.phone),
                  }}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                />
              </Grid>
            </Grid>
            <Box style={{ marginTop: '60px' }}>
              <p>Please <a className={classes.link} href="mailto:gridservices@enphase.com" target="_blank" rel="noopener noreferrer">contact support</a> if you have any problems with your account or you would like to cancel it.</p>
              <Typography style={{ margin: '20px 0 0' }}>
                I have consent for all enrolled sites to provide personal information to this user, and I have permission from this user to provide their personal information. Review Enphase's&nbsp;
                <a href="https://enphase.com/privacy" target="_blank" className={classes.privacyPolicy}>Privacy Policy</a>&nbsp;here.
              </Typography>
            </Box>
            <div className={classes.buttonContainer}>
              <AppButton className={`${classes.button} ${classes.primaryButton}`} type="submit" text="Save Details" />
              <AppButton className={classes.button} onClick={() => setOpenChangePassword(true)} text="Change Password" fill={false} />
            </div>
          </form>
        </Box>
      </Box>
    </div>
  </>
}

const mapStateToProps = ({ auth }) => {
  const { authUser, currentUser, message, messageType, loading } = auth
  return { authUser, currentUser, message, messageType, loading }
}

const mapDispatchToProps = dispatch => ({
  changePassword: payload => dispatch(changePassword(payload)),
  clearMessage: payload => dispatch(clearMessage(payload)),
  getCurrentUser: payload => dispatch(getCurrentUser(payload))
})

export default connect(mapStateToProps, mapDispatchToProps)(PersonalDetails)